// ============================================================
// SideQuests — Firebase bootstrap (LAZY / PER-SERVICE ARCHITECTURE)
//
// WHY THIS FILE CHANGED (first-visit JS performance pass):
//
// BEFORE: this module statically imported firebase/app, firebase/auth,
// firebase/firestore, firebase/storage, firebase/functions and
// firebase/app-check, and called ensureInit() at module load - eagerly
// constructing ALL SIX products. Because App.tsx (the always-mounted shell)
// imports this module, EVERY public route - including pure static pages such
// as /about and /ri/bristol - downloaded and executed the whole Firebase SDK.
//
// AFTER: only firebase/app is a static import (it is small and
// initializeApp must exist before anything else). Every product is a
// *dynamic* import behind a cached accessor, so Rollup emits each service as
// its own async chunk and a route only requests the services it uses:
//
//   getFirebaseApp()        -> firebase/app only
//   getAuthInstance()       -> + firebase/auth
//   getDb()                 -> + firebase/firestore
//   getStorageInstance()    -> + firebase/storage
//   getFunctionsInstance()  -> + firebase/functions
//   firebase/messaging      -> dynamic, from services/notifications.ts
//   firebase/app-check      -> dynamic, only when a genuine Enterprise key is
//                              injected at build time. App Check is currently
//                              DISABLED in production, so this chunk is never
//                              requested at all.
//
// Behaviour is unchanged: same project, same Auth, same Firestore, same
// Storage bucket, same Functions region, same sign-in/sign-out semantics.
// Only the TIMING of module loading changed (lazy instead of eager).
// ============================================================

import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import type { Auth } from 'firebase/auth';
import type { Firestore } from 'firebase/firestore';
import type { FirebaseStorage } from 'firebase/storage';
import type { Functions } from 'firebase/functions';

const firebaseConfig = {
  apiKey: 'AIzaSyDgbwL_U4HvSlJQSsQ1mqfDdobUSi3DKm0',
  // Canonical custom domain. Must be listed in Firebase Auth -> Settings ->
  // Authorized domains for Google sign-in to complete. Never expose the
  // legacy .web.app hosting alias in public output.
  authDomain: 'trysidequests.com',
  projectId: 'bedlaunchpad',
  storageBucket: 'bedlaunchpad.appspot.com',
  messagingSenderId: '958022579454',
  appId: '1:958022579454:web:49b3f3a7639dd78cd161af'
};

// -------------------------------------------------------------
// App Check (reCAPTCHA Enterprise)
//
// FIX (Profile blank-screen incident):
// App Check was initialized with a CLASSIC reCAPTCHA v3 site key
// (`6Lc...`). Firebase App Check only accepts reCAPTCHA ENTERPRISE
// keys - a classic key makes the token exchange return HTTP 403
// and the SDK throttles EVERY Firebase request (auth, Firestore,
// storage, functions) for 24h (appCheck/throttled). That killed
// the Firestore user fetch after sign-in, so the Profile page
// never got its `appUser` and rendered a blank screen.
//
// Resolution:
//  1. App Check is only enabled when a REAL Enterprise site key is
//     provided via VITE_APPCHECK_SITE_KEY at build time.
//  2. The old embedded key is rejected explicitly (it is known to
//     produce the 403/throttle failure above).
//  3. Initialization is wrapped in try/catch so a misconfigured
//     provider can never take down the whole app again.
//  4. The app-check SDK itself is now a DYNAMIC import, so when no
//     genuine key is configured (current production) the SDK is never
//     downloaded. Behavioural result is identical - App Check simply
//     stays off, exactly as before.
//
// Security note: App Check is NOT currently enforced in
// firestore.rules or Cloud Functions, so skipping it when no valid
// key is configured does not change the enforced security posture.
// -------------------------------------------------------------

const APPCHECK_ENTERPRISE_PREFIX = '6L'; // all Enterprise site keys start with 6L
/** The key that caused the 403/throttle outage - never use it. */
const KNOWN_BAD_RECAPTCHA_KEY = '6LcMenAtAAAAANBmwAMMi4b4G2T6H23W1q4zPNRl';

// Vite injects import.meta.env in browser builds. Access through `any`
// to stay compatible with tsconfigs that omit vite/client types.
const viteEnv =
  typeof import.meta !== 'undefined' && (import.meta as any).env
    ? ((import.meta as any).env.VITE_APPCHECK_SITE_KEY as string | undefined)
    : undefined;

// Only a genuine key is accepted. A missing/invalid key leaves App Check OFF
// (safe default - the app keeps working; rules/functions stay unenforced).
const APP_CHECK_SITE_KEY: string | undefined =
  viteEnv && viteEnv.trim() !== KNOWN_BAD_RECAPTCHA_KEY && viteEnv.startsWith(APPCHECK_ENTERPRISE_PREFIX)
    ? viteEnv.trim()
    : undefined;

// -------------------------------------------------------------
// LOCAL FIREBASE EMULATORS (local E2E only)
//
// Gated on VITE_USE_FIREBASE_EMULATORS=true. When the flag is ABSENT
// (production/preview build) NOTHING here runs and the app uses the real
// Firebase backends exactly as before. This is the ONLY place emulator
// connectivity is wired, so a production build can never accidentally reach
// localhost emulator ports.
//
// Dead-code elimination: USE_EMULATORS is read from import.meta.env, which
// Vite statically replaces with undefined in a production build, so esbuild
// folds every `if (USE_EMULATORS)` branch to false and strips the emulator
// connect calls (and the literal localhost/port strings) from the bundle.
// -------------------------------------------------------------

const USE_EMULATORS = (import.meta as any)?.env?.VITE_USE_FIREBASE_EMULATORS === 'true';

let _app: FirebaseApp | null = null;
let _auth: Auth | null = null;
let _db: Firestore | null = null;
let _storage: FirebaseStorage | null = null;
let _functions: Functions | null = null;
let _appCheckDone = false;

/** Minimal, synchronous app bootstrap. Only imports firebase/app. */
export function getFirebaseApp(): FirebaseApp {
  if (_app) return _app;
  _app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];
  return _app;
}

/**
 * App Check: lazily loads firebase/app-check ONLY when a genuine reCAPTCHA
 * Enterprise site key was injected at build time. When no key is configured
 * (current production) nothing is imported and nothing runs.
 */
export function ensureAppCheck(): Promise<void> {
  if (_appCheckDone) return Promise.resolve();
  _appCheckDone = true;
  if (!APP_CHECK_SITE_KEY) return Promise.resolve();
  return import('firebase/app-check')
    .then(({ initializeAppCheck, ReCaptchaV3Provider }) => {
      try {
        initializeAppCheck(getFirebaseApp(), {
          provider: new ReCaptchaV3Provider(APP_CHECK_SITE_KEY as string),
          isTokenAutoRefreshEnabled: true,
        });
        console.log('[SQ] App Check enabled (reCAPTCHA Enterprise).');
      } catch (e: any) {
        // A bad provider must never brick auth/Firestore - fall back to unenforced.
        console.warn('[SQ] App Check init failed - continuing without it:', e?.message || e);
      }
    })
    .catch((e: any) => {
      console.warn('[SQ] App Check SDK failed to load - continuing without it:', e?.message || e);
    });
}

/** Authentication SDK: lazy - loaded by the auth context / auth screens. */
export async function getAuthInstance(): Promise<Auth> {
  if (_auth) return _auth;
  const mod = await import('firebase/auth');
  const app = getFirebaseApp();
  _auth = mod.getAuth(app);
  if (USE_EMULATORS) {
    try {
      const authUrl = (import.meta as any).env?.VITE_FIREBASE_AUTH_EMULATOR_URL || 'http://localhost:9099';
      mod.connectAuthEmulator(_auth, authUrl, { disableWarnings: true });
    } catch (e: any) { console.warn('[SQ] Auth emulator connect failed:', e?.message || e); }
  }
  return _auth;
}

/** Firestore SDK: lazy - loaded by any route that reads/writes data. */
export async function getDb(): Promise<Firestore> {
  if (_db) return _db;
  const mod = await import('firebase/firestore');
  const app = getFirebaseApp();
  _db = mod.getFirestore(app);
  if (USE_EMULATORS) {
    try {
      const fsHost = (import.meta as any).env?.VITE_FIRESTORE_EMULATOR_HOST || 'localhost';
      const fsPort = Number((import.meta as any).env?.VITE_FIRESTORE_EMULATOR_PORT || 8080);
      mod.connectFirestoreEmulator(_db, fsHost, fsPort);
    } catch (e: any) { console.warn('[SQ] Firestore emulator connect failed:', e?.message || e); }
  }
  return _db;
}

/** Cloud Storage SDK: lazy - only upload/download surfaces need it. */
export async function getStorageInstance(): Promise<FirebaseStorage> {
  if (_storage) return _storage;
  const mod = await import('firebase/storage');
  const app = getFirebaseApp();
  _storage = mod.getStorage(app);
  if (USE_EMULATORS) {
    try {
      const stHost = (import.meta as any).env?.VITE_STORAGE_EMULATOR_HOST || 'localhost';
      const stPort = Number((import.meta as any).env?.VITE_STORAGE_EMULATOR_PORT || 9199);
      mod.connectStorageEmulator(_storage, stHost, stPort);
    } catch (e: any) { console.warn('[SQ] Storage emulator connect failed:', e?.message || e); }
  }
  return _storage;
}

/** Callable Functions SDK: lazy - only surfaces that call a Function need it. */
export async function getFunctionsInstance(): Promise<Functions> {
  if (_functions) return _functions;
  const mod = await import('firebase/functions');
  const app = getFirebaseApp();
  _functions = mod.getFunctions(app);
  if (USE_EMULATORS) {
    try {
      const fnHost = (import.meta as any).env?.VITE_FUNCTIONS_EMULATOR_HOST || 'localhost';
      const fnPort = Number((import.meta as any).env?.VITE_FUNCTIONS_EMULATOR_PORT || 5001);
      mod.connectFunctionsEmulator(_functions, fnHost, fnPort);
    } catch (e: any) { console.warn('[SQ] Functions emulator connect failed:', e?.message || e); }
  }
  return _functions;
}

/**
 * Boots the Firebase app (and App Check when configured). Deliberately does
 * NOT construct Auth/Firestore/Storage/Functions - those are built on first
 * real use via the accessors above, so a route that never touches them never
 * downloads their SDK chunks.
 */
export async function initFirebase(): Promise<{ app: FirebaseApp }> {
  const app = getFirebaseApp();
  await ensureAppCheck();
  return { app };
}

/** Convenience: construct every handle on demand. */
export async function getFirebaseServices() {
  const [auth, db, storage, functions] = await Promise.all([
    getAuthInstance(),
    getDb(),
    getStorageInstance(),
    getFunctionsInstance(),
  ]);
  return { app: getFirebaseApp(), auth, db, storage, functions };
}

export type { Auth, Firestore, FirebaseStorage, Functions };
// -------------------------------------------------------------
// SDK API helpers
//
// Each helper returns the service INSTANCE plus that service's SDK module
// namespace in ONE awaited call, so a call site stays small:
//
//   const { db, collection, query, onSnapshot } = await firestoreApi();
//
// These `import()` boundaries are what let Rollup emit firebase/auth,
// firebase/firestore, firebase/storage and firebase/functions as separate
// async chunks that a route only downloads when it actually uses that
// service. Nothing here is a static import of an SDK product.
// -------------------------------------------------------------

export async function authApi() {
  const mod = await import('firebase/auth');
  const auth = await getAuthInstance();
  return { auth, ...mod };
}

export async function firestoreApi() {
  const mod = await import('firebase/firestore');
  const db = await getDb();
  return { db, ...mod };
}

export async function storageApi() {
  const mod = await import('firebase/storage');
  const storage = await getStorageInstance();
  return { storage, ...mod };
}

export async function functionsApi() {
  const mod = await import('firebase/functions');
  const functions = await getFunctionsInstance();
  return { functions, ...mod };
}
/**
 * Returns a callable Firebase Function by name.
 *
 * The `firebase/functions` SDK is loaded lazily on first use, so a route that
 * never calls a Function never downloads that chunk. This keeps every call
 * site to a minimal diff while preserving the exact previous behaviour
 * (including `.data` access on the callable result):
 *
 *   const fn = await callable('myFn');
 *   const res = await fn(payload);
 */
export async function callable(name: string) {
  const { httpsCallable, functions } = await functionsApi();
  return httpsCallable(functions, name);
}
/**
 * Subscription helper for components that build a Firestore listener inside a
 * synchronous React effect (where the effect must return a cleanup function).
 *
 *   useEffect(() => fsSubscribe(({ db, doc, onSnapshot }) => {
 *     return onSnapshot(doc(db, 'x', 'y'), handler);
 *   }), [deps]);
 *
 * The Firestore SDK loads lazily; the returned cancel function disposes either
 * the pending lookup or the live listener. This keeps every existing effect
 * shape (and its synchronous cleanup) intact.
 */
export function fsSubscribe<T>(
  build: (api: Awaited<ReturnType<typeof firestoreApi>>) => T,
  onError?: (err: unknown) => void
): () => void {
  let cancelled = false;
  let live: T | null = null;
  firestoreApi()
    .then((api) => {
      if (cancelled) return;
      live = build(api);
    })
    .catch((err) => onError?.(err));
  return () => {
    cancelled = true;
    const maybe = live as unknown as { (): void } | null;
    if (typeof maybe === 'function') {
      try { maybe(); } catch { /* listener already detached */ }
    }
  };
}
